// import {useRouter} from 'next/router';
import {
  BigNumber,
  formatEther,
  parseEther,
  parseUnits,
  toNativeCurrencyString,
  TransactionResponse,
} from '@ttx/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import {keccak256} from '@ethersproject/keccak256';
// import {VoidSigner} from '@ethersproject/abstract-signer';
import {MerkleTree} from 'merkletreejs';
import {useCallback, useMemo, useState} from 'react';
import Web3 from 'web3';
import {usePrivy} from '@privy-io/react-auth';
import {useMarketplaceContract} from '../components/blockchain-props-wrapper/use-marketplace-contract';
import {PaymentDetailsFormProps} from '../components/purchase-or-download-cta/purchase-cta/cash-payment-details-modal/cash-payment-details-modal';
import {useAuthenticationContext} from '../contexts/authentication-context';
import {WalletNetwork} from '../contexts/authentication-context/types';
import {useSmartContractContext} from '../contexts/smart-contract/context';
import {isWeb3Contract} from '../contexts/smart-contract/utils';
// import {CirclePay} from '../utils/payment/circle-pay-api';
import {usePriceConversion} from './use-price-conversion';
// import {ENVIRONMENT} from '../contexts/authentication-context/connectors';
import {usePaymentStatus} from './use-payment-status';
import {useCheckoutModals} from './use-checkout-modals';
import {useQuantitySelector} from './use-quantity-selector';
import {CashValues, CryptoValues, ModalVariant} from '../utils/types';
import {useTreasuryBalance} from './use-treasury-balance';
import {Tier} from '../server/router/user/types';
import {useTtxMutation} from './ttx-trpc';
import {calculateSquareupFee} from './utils/calculate-squareup-fee';

const TRANSACTION_FEE_PERCENTAGE = BigNumber.from(10); // 10%

// const checkout = new CirclePay();

const MAX_ALLOWED_UNITS = 3;

// eslint-disable-next-line no-shadow
export enum CheckoutButtonType {
  LOADING = 'loading',
  SIGN_UP = 'sign up',
  SOLD_OUT = 'sold out',
  PURCHASE = 'purchase',
  COMING_SOON = 'coming soon',
  FREE = 'Free',
  MINT = 'mint',
  PURCHASE_DISABLE = 'purchase disable',
  CUSTOM = 'custom',
  MEMBERSHIP = 'membership',
  VIEW_ON_POLYMESH_PORTAL = 'to view on polymesh portal',
}

export const useCheckout: (props: {
  slug: string | undefined;
  tokenId?: number;
  network?: WalletNetwork;
  multiCopy?: boolean;
  contractAddress?: string;
  ipAddress?: string;
  url: string;
  trackName?: string;
  artist?: string;
  imageSrc?: string;
  id?: string;
  status?: number;
  primarySaleUserCount?: number;
  isAllowedUser: boolean;
  maxPurchaseLimit: number;
  whiteList: string[];
  isGuestList?: boolean;
  membershipInfo?:
    | {
        isMember: boolean;
        tier: Tier;
        percent: number;
      }
    | undefined;
  country: string;
}) => {
  handleBuyWithCrypto: () => Promise<TransactionResponse>;
  handleBuyWithCash: (values: PaymentDetailsFormProps) => Promise<void>;
  monetaryValues: {
    crypto: CryptoValues;
    cash: CashValues;
  };
  paymentStatus?: string;
  modalVariant: ModalVariant | null;
  modalVariantComponent: JSX.Element | null;
  setModalVariant: (modalType: ModalVariant | null) => void;
  handleStartCheckoutFlow: () => void;
  checkoutButtonType: CheckoutButtonType;
} = ({
  slug,
  tokenId,
  network,
  multiCopy,
  contractAddress,
  // ipAddress,
  url,
  trackName,
  artist,
  imageSrc,
  id,
  status,
  primarySaleUserCount,
  isAllowedUser,
  maxPurchaseLimit,
  whiteList,
  isGuestList,
  membershipInfo,
  country,
}) => {
  const {ready} = usePrivy();
  const privyNetwork = ready ? network : null;
  const {walletAddress, walletNetwork, walletProvider} =
    useAuthenticationContext();

  const {...adminSignMessage} = useTtxMutation('evm.admin-sign-message');
  // Discount for selected [drops | users] endpoint
  const {...discountSignMessage} = useTtxMutation('evm.drop-discount');
  const {paymentStatus, userEmail} = usePaymentStatus();
  const [modalVariant, setModalVariant] = useState<ModalVariant | null>(null);
  const {
    marketplaceContract,
    getPaymentTokenContract,
    getMaxCurrencyAllowanceFromAddress,
    getCanAskForAllowance,
  } = useSmartContractContext();

  const {
    currency,
    listingId,
    buyoutPrice: buyPrice,
    currencySymbol,
    quantity,
    loading,
  } = useMarketplaceContract({
    tokenId,
    network,
    multiCopy,
    contractAddress,
  });
  const {isMember, percent} = membershipInfo || {
    isMember: false,
    percent: 0,
  };

  const formattedPrice = buyPrice
    ? Number(toNativeCurrencyString(buyPrice, currencySymbol))
    : 0;
  const isMembershipNFT =
    slug?.includes('membership') && slug !== 'my-sahel-membership-nft';
  const discountValue = isMembershipNFT
    ? 0
    : formattedPrice * (percent / 100) || 0;
  // Price formatted to 6 digits
  const buyoutPrice = BigNumber.from(
    parseUnits((formattedPrice - discountValue).toFixed(6).toString(), 18),
  );

  const {convertCryptoToUSD} = usePriceConversion({
    primaryCurrency: currencySymbol,
    shouldPoll: modalVariant === 'cash-payment-details',
  });

  // const {convertCryptoToUSD: convertMATICToUSD} = usePriceConversion({
  //   primaryCurrency: 'MATIC',
  //   shouldPoll: false,
  // });

  const {convertCryptoToUSD: convertETHToUSD} = usePriceConversion({
    primaryCurrency: 'ETH',
    shouldPoll: false,
  });

  const {
    canUseCash,
    maxQuantityAvailableToPurchase,
    isLoading: loadingTreasuryBalance,
  } = useTreasuryBalance({
    amount: BigNumber.from(buyoutPrice || 0),
    currency: currencySymbol,
    network,
  });

  const studioPageMsg = [
    'ep1-ryan-edition',
    'ep1-tommy-edition',
    'ep1-pedro-edition',
    'ep1-zach-edition',
    'ep1-james-edition',
    'ep1-bundle',
  ];

  let platformMaxLimit =
    slug && studioPageMsg.includes(slug) ? 100 : MAX_ALLOWED_UNITS;

  if (slug === 'my-sahel-membership-nft') {
    platformMaxLimit = 1000;
  }

  if (slug === 'head-over-heels') {
    platformMaxLimit = 100;
  }
  const {onIncrement, onDecrement, onResetQuantity, purchaseQuantity} =
    useQuantitySelector({
      availableQuantity: Number(quantity),
      maxQuantity: isGuestList ? 1 : platformMaxLimit,
      maxQuantityAvailableToPurchase,
      isCashModal: modalVariant === 'cash-payment-details',
    });

  const monetaryValues = useMemo(() => {
    if (!buyoutPrice || !currencySymbol)
      return {
        crypto: {
          price: null,
          totalPerNFT: null,
          total: null,
          transactionFee: null,
          currencySymbol: null,
          originalPrice: null,
        },
        cash: {
          price: null,
          totalPerNFT: null,
          total: null,
          transactionFee: null,
          currencySymbol: null,
          originalPrice: null,
          squareupTransactionFee: null
        },
      };

    const pricePerNFT = BigNumber.from(buyoutPrice);
    const totalCost = pricePerNFT.mul(purchaseQuantity);
    const transactionFeeCost = totalCost
      .mul(TRANSACTION_FEE_PERCENTAGE)
      .div(BigNumber.from(100));
    const nftCost = totalCost.sub(transactionFeeCost);

    // for squareup
    const squareupFee = calculateSquareupFee(
      Number(convertCryptoToUSD(totalCost)?.replace('$', '')),
    );

    return {
      crypto: {
        price: toNativeCurrencyString(nftCost, currencySymbol),
        totalPerNFT: toNativeCurrencyString(pricePerNFT, currencySymbol),
        total: toNativeCurrencyString(totalCost, currencySymbol),
        transactionFee: toNativeCurrencyString(
          transactionFeeCost,
          currencySymbol,
        ),
        currencySymbol,
        originalPrice: toNativeCurrencyString(buyPrice, currencySymbol),
      },
      cash: {
        price: convertCryptoToUSD(nftCost),
        totalPerNFT: convertCryptoToUSD(pricePerNFT),
        total: squareupFee.total,
        transactionFee: convertCryptoToUSD(transactionFeeCost),
        currencySymbol: 'USDT',
        originalPrice: convertCryptoToUSD(buyPrice),
        squareupTransactionFee: squareupFee.fee,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyoutPrice, convertCryptoToUSD, currencySymbol, purchaseQuantity]);

  const {isSold, tokenOwner} = useMarketplaceContract({
    tokenId,
    network,
    multiCopy,
    contractAddress,
  });

  const checkoutButtonType = useMemo(() => {
    if (!canUseCash && walletProvider === 'venly')
      return CheckoutButtonType.PURCHASE_DISABLE;
    if (loading || loadingTreasuryBalance) return CheckoutButtonType.LOADING;
    if (status === 1) return CheckoutButtonType.SIGN_UP;
    if (isSold) return CheckoutButtonType.SOLD_OUT;
    if (!buyPrice) return CheckoutButtonType.COMING_SOON;
    if (!Number(monetaryValues.crypto.price)) return CheckoutButtonType.FREE;
    return CheckoutButtonType.PURCHASE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    loadingTreasuryBalance,
    status,
    isSold,
    buyoutPrice,
    monetaryValues.crypto.price,
    canUseCash,
    walletProvider,
  ]);

  const handleBuyWithCrypto = useCallback(async () => {
    if (!walletNetwork) throw new Error('Network is not defined');
    if (!walletAddress) throw new Error('Wallet account is not defined');

    const loadedMarketplaceContract = await marketplaceContract(walletNetwork);

    if (!loadedMarketplaceContract)
      throw new Error('Could not load marketplace contract');

    const loadedCurrencyContract = await getPaymentTokenContract(currency);

    if (!loadedCurrencyContract)
      throw new Error('Could not load currency contract');

    const maxAllowance = getMaxCurrencyAllowanceFromAddress(currency);

    if (!maxAllowance) throw new Error('Could not get max allowance');

    // TODO: remove this when Zilliqa-equivalent is implemented
    if (!isWeb3Contract(loadedCurrencyContract))
      throw new Error('Only support web3 contracts right now...');

    // TODO: remove this when Zilliqa-equivalent is implemented
    if (!isWeb3Contract(loadedMarketplaceContract))
      throw new Error('Only support web3 contracts right now...');

    const canAskForAllowance = await getCanAskForAllowance(currency);

    // set proof for the purchase collection whitelistParams
    let proof: string[] = [];
    if (isAllowedUser && whiteList.length > 0) {
      const tree = new MerkleTree(
        whiteList.map((x) => keccak256(x)),
        keccak256,
        {sortPairs: true},
      );
      const leaf = keccak256(walletAddress);
      const buf2hex = (buffer: Buffer) => {
        const bufferArray = new Uint8Array(buffer);
        return Array.from(bufferArray)
          .map((x) => x.toString(16).padStart(2, '0'))
          .join('');
      };
      proof = tree.getProof(leaf).map((x) => `0x${buf2hex(x.data)}`);
    }

    let sigHash = '0x';
    const currentStamp = Date.now();
    // TODO: Rewrite in more generic way
    // Skip the Kena Rose artist Life Without Water drop
    // because of the discount
    if (isMember && !isMembershipNFT && slug !== 'life-without-water') {
      sigHash = (await adminSignMessage.mutateAsync({
        timeStamp: currentStamp,
      })) as unknown as string;
    }
    // TODO: Rewrite in more generic way
    if (slug === 'life-without-water') {
      try {
        sigHash = (await discountSignMessage.mutateAsync({
          timeStamp: currentStamp,
        })) as unknown as string;
      } catch (e) {
        sigHash = '0x';
      }
    }

    const buyNft = () => {
      return loadedMarketplaceContract.buy(
        listingId,
        currency,
        buyoutPrice,
        BigNumber.from(purchaseQuantity),
        proof,
        currentStamp,
        sigHash,
        canAskForAllowance
          ? {}
          : {value: BigNumber.from(buyoutPrice).mul(purchaseQuantity)},
      );
    };

    if (canAskForAllowance) {
      const availableAllowance = await loadedCurrencyContract.allowance(
        walletAddress,
        loadedMarketplaceContract.address,
      );
      const allowance: string = availableAllowance.toString();
      const stakedAmount: string = formatEther(allowance);
      const readableAllowance: BigNumber = parseEther(stakedAmount);
      if (
        readableAllowance.lt(BigNumber.from(buyoutPrice).mul(purchaseQuantity))
      ) {
        const approvalTransaction = (await loadedCurrencyContract.approve(
          loadedMarketplaceContract.address,
          maxAllowance,
        )) as TransactionResponse;

        // Await confirmation of approval transaction
        return approvalTransaction.wait().then(buyNft);
      }
    }

    return buyNft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    walletNetwork,
    walletAddress,
    marketplaceContract,
    getPaymentTokenContract,
    currency,
    getMaxCurrencyAllowanceFromAddress,
    getCanAskForAllowance,
    isAllowedUser,
    whiteList,
    listingId,
    buyoutPrice,
    purchaseQuantity,
    isMember,
  ]);

  const handleEstimateGasFee = useCallback(async () => {
    const currentNetwork = walletNetwork || privyNetwork;
    if (!currentNetwork) throw new Error('Network is not defined');
    if (!walletAddress) throw new Error('Wallet account is not defined');

    const loadedMarketplaceContract = await marketplaceContract(currentNetwork);

    if (!loadedMarketplaceContract)
      throw new Error('Could not load marketplace contract');

    // const loadedCurrencyContract = await getPaymentTokenContract(currency);

    // if (!loadedCurrencyContract)
    //   throw new Error('Could not load currency contract');

    // const maxAllowance = getMaxCurrencyAllowanceFromAddress(currency);

    // if (!maxAllowance) throw new Error('Could not get max allowance');

    // // TODO: remove this when Zilliqa-equivalent is implemented
    // if (!isWeb3Contract(loadedCurrencyContract))
    //   throw new Error('Only support web3 contracts right now...');

    // TODO: remove this when Zilliqa-equivalent is implemented
    if (!isWeb3Contract(loadedMarketplaceContract))
      throw new Error('Only support web3 contracts right now...');

    // const canAskForAllowance = await getCanAskForAllowance(currency);

    // set proof for the purchase collection whitelistParams
    let proof: string[] = [];
    if (isAllowedUser && whiteList.length > 0) {
      const tree = new MerkleTree(
        whiteList.map((x) => keccak256(x)),
        keccak256,
        {sortPairs: true},
      );
      const leaf = keccak256(walletAddress);
      const buf2hex = (buffer: Buffer) => {
        const bufferArray = new Uint8Array(buffer);
        return Array.from(bufferArray)
          .map((x) => x.toString(16).padStart(2, '0'))
          .join('');
      };
      proof = tree.getProof(leaf).map((x) => `0x${buf2hex(x.data)}`);
    }

    // let sigHash = '0x';
    // const currentStamp = Date.now();

    // const isMembershipNFTCheck = slug?.includes('membership');
    // if (isMember && !isMembershipNFTCheck) {
    //   sigHash = (await adminSignMessage.mutateAsync({
    //     timeStamp: currentStamp,
    //   })) as unknown as string;
    // }

    // const buyNft = async () => {
    //   try {
    //     return loadedMarketplaceContract
    //       .connect(
    //         new VoidSigner(
    //           process.env.ADMIN_ADDRESS as string,
    //           loadedMarketplaceContract.provider as any,
    //         ),
    //       )
    //       .estimateGas.delegatedBuy(
    //         listingId,
    //         currency,
    //         buyoutPrice,
    //         BigNumber.from(purchaseQuantity),
    //         walletAddress,
    //         proof,
    //         currentStamp,
    //         sigHash,
    //         canAskForAllowance
    //           ? {}
    //           : {value: BigNumber.from(buyoutPrice).mul(purchaseQuantity)},
    //       );
    //   } catch (e) {
    //     return '200000';
    //   }
    // };

    const gasPrice = await loadedMarketplaceContract.provider.getGasPrice();
    const totalGas = gasPrice.mul(1); // adjust gas price as required
    const gasPriceEth = Number(
      Web3.utils.fromWei(totalGas.toString(), 'ether'),
    ).toFixed(4);

    return {
      gasEstimate: convertETHToUSD(gasPrice.mul(Number(gasPriceEth))),
    };

    // if (walletNetwork === 'polygon')
    //   return convertMATICToUSD(gasPrice.mul(await buyNft()));
    // if (walletNetwork === 'ethereum')
    //   return convertETHToUSD(gasPrice.mul(await buyNft()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    walletNetwork,
    walletAddress,
    marketplaceContract,
    getPaymentTokenContract,
    currency,
    getMaxCurrencyAllowanceFromAddress,
    getCanAskForAllowance,
    isAllowedUser,
    whiteList,
    listingId,
    buyoutPrice,
    purchaseQuantity,
    isMember,
  ]);

  const handleBuyWithCash = async (values: PaymentDetailsFormProps) => {
    // setModalVariant('cash-pending');
    // if (!values || !ipAddress || !monetaryValues.cash.total || !url)
    //   throw new Error('Missing values required for checkout');
    // try {
    //   const {sessionId} = checkout;
    //   const encryptedCardDetails = await checkout.encryptCardDetails({
    //     number: values.cardNumber.trim(),
    //     cvv: values.cvv,
    //   });
    //   const metadata = {
    //     email: values.email,
    //     sessionId,
    //     ipAddress,
    //   };
    //   const details = {
    //     billingDetails: {
    //       name: values.cardholderName,
    //       city: values.city,
    //       country: values.country,
    //       district: values.state,
    //       line1: values.billingLine1,
    //       line2: values.billingLine2,
    //       postalCode: values.postCode,
    //     },
    //     metadata,
    //     encryptedData: encryptedCardDetails.encryptedMessage,
    //     keyId: encryptedCardDetails.keyId,
    //     expMonth: Number(values.expiryMonth),
    //     expYear:
    //       values.expiryYear?.length === 2
    //         ? Number(`20${values.expiryYear}`)
    //         : Number(values.expiryYear),
    //   };
    //   const paymentDetails = {
    //     metadata,
    //     amount: {
    //       amount: monetaryValues.cash.total?.replace('$', ''),
    //       currency: 'USD',
    //     },
    //     autoCapture: true,
    //     keyId: encryptedCardDetails.keyId,
    //     // The verification redirect only works on hosted envs so for local, we don't use 3D secure
    //     verification: ENVIRONMENT === 'local' ? 'none' : 'three_d_secure',
    //     verificationSuccessUrl: ENVIRONMENT === 'local' ? '' : `https://${url}`,
    //     verificationFailureUrl: ENVIRONMENT === 'local' ? '' : `https://${url}`,
    //     description: JSON.stringify({
    //       userId,
    //       nftId: id,
    //       listingId: Number(listingId).toString(),
    //       quantity: purchaseQuantity,
    //       price: buyoutPrice?.toString(),
    //       dropType: 'normal',
    //     }),
    //     idempotencyKey: sessionId,
    //   };
    //   const card = await checkout.createCard(details);
    //   await checkout
    //     .createPayment({
    //       ...paymentDetails,
    //       source: {type: 'card', id: card.id},
    //     })
    //     .then((data) => {
    //       // For local envs only - automatically handled by 3DS in hosted envs
    //       if (data.id) {
    //         router.push(`/tracks/${slug}?paymentId=${data.id}`);
    //       }
    //     });
    // } catch (e: any) {
    //   setModalVariant('cash-failed');
    // }
  };

  const {modalVariantComponent, onPressPurchase} = useCheckoutModals({
    currencySymbol,
    network,
    modalVariant,
    setModalVariant,
    canUseCash,
    props: {
      paymentStatus,
      onStartCashCheckout: handleBuyWithCash,
      onStartCryptoCheckout: handleBuyWithCrypto,
      onEstimateGasFee: handleEstimateGasFee,
      quantity: purchaseQuantity,
      trackName,
      artist,
      imageSrc,
      userEmail,
      ...monetaryValues,
      onIncrement,
      onDecrement,
      onResetQuantity,
      isMultiCopy: multiCopy,
      loading,
      primarySaleUserCount,
      isAllowedUser,
      maxPurchaseLimit,
      buyoutPrice: buyoutPrice.toString(),
      nftId: id || '',
      listingId: Number(listingId).toString(),
      url,
      slug: slug || '',
      country,
      whiteList,
      currency,
      tokenId
    },
    tokenOwner,
    isMember,
  });

  return {
    handleBuyWithCrypto,
    handleBuyWithCash,
    handleEstimateGasFee,
    monetaryValues,
    paymentStatus,
    modalVariant,
    modalVariantComponent,
    setModalVariant,
    handleStartCheckoutFlow: onPressPurchase,
    checkoutButtonType,
  };
};
